import { BASE_URL } from "@/lib/constants";
import { EventType, LevelType, LocationType } from "@prisma/client";
import { zonedTimeToUtc } from "date-fns-tz";
import { z } from "zod";

const EventLocationType = z.nativeEnum(LocationType);
// TODO: The EventLocationType declared below should be removed and
// instead the prisma declared LocationType should be renamed to EventLocationType and then used.
export type EventLocationType = z.infer<typeof EventLocationType>;

export const EventTypeSchema = z.nativeEnum(EventType);
export { EventType } from "@prisma/client";

export const EventLevel = z.nativeEnum(LevelType);
// TODO: Instead of declaring EventLevel below, we should rename the prisma LevelType to EventLevel
// and use directly from there
export type EventLevel = z.infer<typeof EventLevel>;

const RegistrationType = z.enum(["PTB", "EXTERNAL", "NONE"]);
export type RegistrationType = z.infer<typeof RegistrationType>;

const TRawEventDataSchema = z.object({
	peopleCount: z.number().optional(),
	isPremium: z.boolean().optional(),
	registrationType: RegistrationType,
	address: z
		.object({
			fullAddress: z.string().optional(),
			city: z.string().optional(),
			state: z.string().optional(),
			country: z.string().optional(),
			venueName: z.string().optional(),
			gmapLink: z.string().optional(),
		})
		.optional(),
	landingPage: z
		.object({
			title: z.string().optional(),
			subTitle: z.string().optional(),
			description: z.string().optional(),
		})
		.optional(),
});

export type TRawEventData = z.infer<typeof TRawEventDataSchema>;

export const CommunityEventSchema = z
	.object({
		communitySlug: z.string().optional().nullable(),
		title: z
			.string()
			.min(2, "Title length must be above 2 characters")
			.max(100, "Title length must be less than 100 characters"),
		description: z.string(),
		dateTime: z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "Invalid dateTime format"),
		endDateTime: z
			.string()
			.regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/, "Invalid endDateTime format")
			.optional(),
		timezone: z.string().min(1, "Please select a timezone"),
		location: EventLocationType,
		type: EventTypeSchema,
		level: EventLevel,
		isPrivate: z.boolean(),
		coverImage: z.string().optional(),
		tagline: z.string(),
		isFeatured: z.boolean().optional(),
		externalLink: z.string().optional(),
		organizerName: z.string().optional(),
		rawEventData: TRawEventDataSchema,
		tags: z.array(z.string()),
		link: z.string().min(2, "Link cannot be empty").url().optional(),
	})
	.refine(
		(data) => {
			const utcDate = zonedTimeToUtc(data.dateTime, data.timezone);
			return utcDate >= new Date();
		},
		{
			message: "Start date must be today or in the future in the selected timezone.",
			path: ["dateTime"],
		}
	)
	.refine(
		(data) => {
			if (data.endDateTime) {
				const startDate = zonedTimeToUtc(data.dateTime, data.timezone);
				const endDate = zonedTimeToUtc(data.endDateTime, data.timezone);
				return endDate >= startDate;
			}
			return true;
		},
		{
			message: "End date cannot be before the start date.",
			path: ["endDateTime"],
		}
	);

export type TEventFormData = z.infer<typeof CommunityEventSchema>;

export type TEvent = Omit<TEventFormData, "endDateTime"> & {
	readonly id: string;
	slug: string;
	totalRegistrations?: number | null;
	totalViews?: number | null;
	createdAt?: Date;
	updatedAt?: Date;
	hasUserRegisteredToTheEvent?: boolean;
	peopleCount?: number;
	isPremium?: boolean;
	endDateTime?: string;
	authorId?: string;
};

export interface GetEventsResponseDTO {
	success: boolean;
	message: string;
	data: {
		events: TEvent[];
		total: number;
	};
}

export interface Filters {
	eventType: string;
	location: string;
	searchTerm: string;
}

export function extractEventLink(event: TEvent) {
	let eventURL = "";
	if (event.rawEventData?.registrationType === "EXTERNAL" && event.externalLink) {
		eventURL = event.externalLink;
	} else if (event.rawEventData?.registrationType === "PTB") {
		eventURL = `${BASE_URL}/events/${event.slug}`;
	} else if (event.rawEventData?.registrationType === "NONE" && event.link) {
		eventURL = event.link;
	}
	return eventURL;
}
